
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import MainLayout from "@/components/layout/MainLayout";
import { Card } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { MemberReviewCard } from "@/components/reviews/MemberReviewCard";
import { Review } from "@/types/review";
import { useLanguage } from "@/context/LanguageContext";

const MemberReviews = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/login");
        return;
      }
      fetchUserReviews(session.user.id);
    };

    checkAuth();
  }, [navigate]);

  const fetchUserReviews = async (userId: string) => {
    try {
      setLoading(true);
      console.log("Fetching reviews for user:", userId);
      
      const { data, error } = await supabase
        .from("reviews")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching reviews:", error);
        throw error;
      }
      
      console.log("Fetched reviews:", data);
      
      const typedReviews = data?.map(review => {
        let coords = { lat: 25.0330, lng: 121.5654 }; // Default Taipei coordinates
        
        if (review.coordinates) {
          try {
            if (typeof review.coordinates === 'object') {
              const coordinates = review.coordinates as { lat?: number; lng?: number };
              const lat = coordinates.lat !== undefined ? 
                Number(coordinates.lat) : 25.0330;
              const lng = coordinates.lng !== undefined ? 
                Number(coordinates.lng) : 121.5654;
              coords = { lat, lng };
            }
          } catch (e) {
            console.error("Error parsing coordinates:", e);
          }
        }
        
        return {
          ...review,
          deposit_type: review.deposit_type as "amount" | "months",
          coordinates: coords,
          country: review.country || "CA",
          region: review.region || "AB",
          includes_utilities: review.includes_utilities || false,
          viewing_during_lease: review.viewing_during_lease || false,
          rental_status: review.rental_status as "viewed" | "renting" | "ended"
        } as Review;
      }) || [];
      
      setReviews(typedReviews);
    } catch (error: any) {
      console.error("Error fetching reviews:", error);
      toast({
        variant: "destructive",
        title: t('errorFetchingReviews'),
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (reviewId: string) => {
    try {
      const { error } = await supabase
        .from("reviews")
        .delete()
        .eq("id", reviewId);

      if (error) throw error;

      setReviews(prev => prev.filter(review => review.id !== reviewId));
      toast({
        title: t('delete'),
        description: t('confirmDeleteBtn'),
      });
    } catch (error: any) {
      console.error("Error deleting review:", error);
      toast({
        variant: "destructive",
        title: t('error'),
        description: error.message,
      });
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="container py-12">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="w-full h-32 animate-pulse bg-muted" />
            ))}
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container py-12">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold">{t('myReviews')}</h1>
          <Button onClick={() => navigate("/submit")}>{t('submitReview')}</Button>
        </div>
        {reviews.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">{t('noReviewsYet')}</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate("/submit")}
            >
              {t('createFirstReview')}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {reviews.map((review) => (
              <MemberReviewCard
                key={review.id}
                review={review}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default MemberReviews;
